import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  UserPlus, Users, Calendar, DollarSign, FileText,
  Search, Filter, Download, Mail, Phone, MapPin,
  Edit, Trash2, Eye, CheckCircle2, XCircle, Clock
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';

const HRManagement = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [newEmployee, setNewEmployee] = useState({
    full_name: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    hire_date: '',
    salary: ''
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error(i18n.language === 'ar' ? 'غير مصرح' : 'Unauthorized');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, leaveRes] = await Promise.all([
        api.get('/hr/employees'),
        api.get('/hr/leave-requests')
      ]);
      setEmployees(empRes.data);
      setLeaveRequests(leaveRes.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/employees', newEmployee);
      toast.success(i18n.language === 'ar' ? 'تم إضافة الموظف بنجاح' : 'Employee added successfully');
      setShowAddEmployee(false);
      setNewEmployee({
        full_name: '',
        email: '',
        phone: '',
        department: '',
        position: '',
        hire_date: '',
        salary: ''
      });
      fetchData();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إضافة الموظف' : 'Failed to add employee');
    }
  };

  const stats = [
    {
      title: i18n.language === 'ar' ? 'إجمالي الموظفين' : 'Total Employees',
      value: employees.length,
      icon: Users,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: i18n.language === 'ar' ? 'طلبات الإجازة' : 'Leave Requests',
      value: leaveRequests.filter(l => l.status === 'pending').length,
      icon: Calendar,
      color: 'from-yellow-500 to-yellow-600'
    },
    {
      title: i18n.language === 'ar' ? 'الموظفون النشطون' : 'Active Employees',
      value: employees.filter(e => e.is_active).length,
      icon: CheckCircle2,
      color: 'from-green-500 to-green-600'
    },
  ];

  const filteredEmployees = employees.filter(emp =>
    emp.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.department.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                {i18n.language === 'ar' ? 'إدارة الموارد البشرية' : 'HR Management'}
              </h1>
              <p className="text-text-secondary text-lg">
                {i18n.language === 'ar' ? 'إدارة الموظفين والرواتب والإجازات' : 'Manage employees, payroll and leaves'}
              </p>
            </div>
            <Dialog open={showAddEmployee} onOpenChange={setShowAddEmployee}>
              <DialogTrigger asChild>
                <Button className="btn-primary gap-2">
                  <UserPlus className="w-5 h-5" />
                  {i18n.language === 'ar' ? 'إضافة موظف' : 'Add Employee'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{i18n.language === 'ar' ? 'إضافة موظف جديد' : 'Add New Employee'}</DialogTitle>
                  <DialogDescription>
                    {i18n.language === 'ar' ? 'أدخل بيانات الموظف الجديد' : 'Enter new employee information'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddEmployee} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{i18n.language === 'ar' ? 'الاسم الكامل' : 'Full Name'}</Label>
                      <Input
                        value={newEmployee.full_name}
                        onChange={(e) => setNewEmployee({...newEmployee, full_name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label>
                      <Input
                        type="email"
                        value={newEmployee.email}
                        onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'رقم الجوال' : 'Phone'}</Label>
                      <Input
                        value={newEmployee.phone}
                        onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'القسم' : 'Department'}</Label>
                      <Input
                        value={newEmployee.department}
                        onChange={(e) => setNewEmployee({...newEmployee, department: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'المنصب' : 'Position'}</Label>
                      <Input
                        value={newEmployee.position}
                        onChange={(e) => setNewEmployee({...newEmployee, position: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'تاريخ التوظيف' : 'Hire Date'}</Label>
                      <Input
                        type="date"
                        value={newEmployee.hire_date}
                        onChange={(e) => setNewEmployee({...newEmployee, hire_date: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'الراتب' : 'Salary'}</Label>
                      <Input
                        type="number"
                        value={newEmployee.salary}
                        onChange={(e) => setNewEmployee({...newEmployee, salary: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setShowAddEmployee(false)}>
                      {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button type="submit" className="btn-primary">
                      {i18n.language === 'ar' ? 'إضافة' : 'Add'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                <CardContent className="pt-6 relative z-10">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-text-secondary mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Tabs */}
          <Tabs defaultValue="employees" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="employees">
                {i18n.language === 'ar' ? 'الموظفون' : 'Employees'}
              </TabsTrigger>
              <TabsTrigger value="leaves">
                {i18n.language === 'ar' ? 'الإجازات' : 'Leaves'}
              </TabsTrigger>
              <TabsTrigger value="payroll">
                {i18n.language === 'ar' ? 'الرواتب' : 'Payroll'}
              </TabsTrigger>
            </TabsList>

            {/* Employees Tab */}
            <TabsContent value="employees" className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                  <Input
                    placeholder={i18n.language === 'ar' ? 'بحث في الموظفين...' : 'Search employees...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredEmployees.map((employee) => (
                  <Card key={employee.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{employee.full_name}</CardTitle>
                          <CardDescription>{employee.position}</CardDescription>
                        </div>
                        <Badge variant={employee.is_active ? 'default' : 'secondary'}>
                          {employee.is_active 
                            ? (i18n.language === 'ar' ? 'نشط' : 'Active')
                            : (i18n.language === 'ar' ? 'غير نشط' : 'Inactive')
                          }
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-text-secondary" />
                        <span>{employee.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-text-secondary" />
                        <span>{employee.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-text-secondary" />
                        <span>{employee.department}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm font-medium text-primary">
                        <DollarSign className="w-4 h-4" />
                        <span>{employee.salary.toLocaleString()} {i18n.language === 'ar' ? 'ريال' : 'SAR'}</span>
                      </div>
                      <div className="flex gap-2 pt-3">
                        <Button size="sm" variant="outline" className="flex-1">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Leaves Tab */}
            <TabsContent value="leaves" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'طلبات الإجازات' : 'Leave Requests'}</CardTitle>
                </CardHeader>
                <CardContent>
                  {leaveRequests.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar className="w-16 h-16 text-text-secondary mx-auto mb-4" />
                      <p className="text-text-secondary">
                        {i18n.language === 'ar' ? 'لا توجد طلبات إجازة' : 'No leave requests'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {leaveRequests.map((leave) => (
                        <div key={leave.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <p className="font-semibold">{leave.leave_type}</p>
                              <Badge variant={
                                leave.status === 'approved' ? 'default' :
                                leave.status === 'rejected' ? 'destructive' : 'secondary'
                              }>
                                {leave.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-text-secondary">
                              {format(new Date(leave.start_date), 'dd/MM/yyyy')} - {format(new Date(leave.end_date), 'dd/MM/yyyy')}
                            </p>
                            <p className="text-sm mt-1">{leave.reason}</p>
                          </div>
                          {leave.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button size="sm" className="bg-green-600 hover:bg-green-700">
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="destructive">
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Payroll Tab */}
            <TabsContent value="payroll" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'إدارة الرواتب' : 'Payroll Management'}</CardTitle>
                  <CardDescription>
                    {i18n.language === 'ar' ? 'قيد التطوير' : 'Coming soon'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12">
                    <DollarSign className="w-16 h-16 text-text-secondary mx-auto mb-4" />
                    <p className="text-text-secondary">
                      {i18n.language === 'ar' ? 'نظام الرواتب قيد التطوير' : 'Payroll system coming soon'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HRManagement;
